// Global variables to track API loading states
window.youtubeApiLoaded = false;
window.vimeoApiLoaded = false;

if (document.querySelector('youtube-video')) {
  // Load YouTube Iframe API
  const youtubeTag = document.createElement('script');
  youtubeTag.src = 'https://www.youtube.com/iframe_api';
  document.head.appendChild(youtubeTag);

  // Callback when YouTube API is ready
  function onYouTubeIframeAPIReady() {
    window.youtubeApiLoaded = true;
  }
}

if (document.querySelector('vimeo-video')) {
  // Load Vimeo Player API
  const vimeoTag = document.createElement('script');
  vimeoTag.src = 'https://player.vimeo.com/api/player.js';
  vimeoTag.onload = function() {
    window.vimeoApiLoaded = true;
  };
  document.head.appendChild(vimeoTag);
}

// Self-Hosted Video Component
class SelfHostedVideoComponent extends HTMLElement {
  connectedCallback() {
    const settings = JSON.parse(this.dataset.settings);
    const videos = {
      desktop: this.querySelector('.js-self-hosted[data-type="desktop"]'),
      mobile: this.querySelector('.js-self-hosted[data-type="mobile"]')
    };

    // Handle autoplay for visible videos
    for (const type in videos) {
      const video = videos[type];
      if (video && window.getComputedStyle(video).display !== 'none' && settings.autoplay) {
        video.play();
      }
    }

    // Attach event listeners to control buttons
    const buttons = this.querySelectorAll('.self-hosted-control');
    buttons.forEach(btn => {
      const target = btn.dataset.target;
      const video = videos[target];
      if (!video) return;

      if (btn.classList.contains('self-hosted-play-pause')) {
        btn.addEventListener('click', () => {
          if (video.paused) {
            video.play();
            btn.innerHTML = window.ICON_SVGS.pause;
          } else {
            video.pause();
            btn.innerHTML = window.ICON_SVGS.play;
          }
        });
      } else if (btn.classList.contains('self-hosted-mute-unmute')) {
        btn.addEventListener('click', () => {
          video.muted = !video.muted;
          btn.innerHTML = video.muted ?  window.ICON_SVGS.mute : window.ICON_SVGS.sound;
        });
      }
    });

    // Update button icons based on video state changes
    for (const type in videos) {
      const video = videos[type];
      if (video) {
        video.addEventListener('play', () => {
          const playBtn = this.querySelector(`.self-hosted-play-pause[data-target="${type}"]`);
          if (playBtn) playBtn.innerHTML = window.ICON_SVGS.pause;
        });
        video.addEventListener('pause', () => {
          const playBtn = this.querySelector(`.self-hosted-play-pause[data-target="${type}"]`);
          if (playBtn) playBtn.innerHTML = window.ICON_SVGS.play;
        });
        video.addEventListener('volumechange', () => {
          const muteBtn = this.querySelector(`.self-hosted-mute-unmute[data-target="${type}"]`);
          if (muteBtn) muteBtn.innerHTML = video.muted ? window.ICON_SVGS.mute : window.ICON_SVGS.sound;
        });
      }
    }
  }
}
customElements.define('self-hosted-video', SelfHostedVideoComponent);

// YouTube Video Component
class YouTubeVideoComponent extends HTMLElement {
  connectedCallback() {
    if (!window.youtubeApiLoaded) {
      // Wait for the API to load
      const interval = setInterval(() => {
        if (window.youtubeApiLoaded) {
          clearInterval(interval);
          this.initYouTubePlayer();
        }
      }, 100);
    } else {
      this.initYouTubePlayer();
    }
  }

  initYouTubePlayer() {
    const settings = JSON.parse(this.dataset.settings);
    const iframe = this.querySelector('.js-youtube');
    const player = new YT.Player(iframe, {
      events: {
        onReady: (event) => {
          if (settings.muted) {
            event.target.mute();
          }
          if (settings.autoplay) {
            event.target.playVideo();
          }
        },
        onStateChange: (event) => {
          const playBtn = this.querySelector('.youtube-play-pause');
          if (playBtn) {
            playBtn.innerHTML = event.data === YT.PlayerState.PLAYING ? window.ICON_SVGS.pause : window.ICON_SVGS.play;
          }
        }
      }
    });

    const playBtn = this.querySelector('.youtube-play-pause');
    if (playBtn) {
      playBtn.addEventListener('click', () => {
        if (player.getPlayerState() === YT.PlayerState.PLAYING) {
          player.pauseVideo();
        } else {
          player.playVideo();
        }
      });
    }

    const muteBtn = this.querySelector('.youtube-mute-unmute');
    if (muteBtn) {
      muteBtn.addEventListener('click', () => {
        if (player.isMuted()) {
          player.unMute();
          muteBtn.innerHTML = window.ICON_SVGS.sound;
        } else {
          player.mute();
          muteBtn.innerHTML = window.ICON_SVGS.mute;
        }
      });
    }
  }
}
customElements.define('youtube-video', YouTubeVideoComponent);

// Vimeo Video Component
class VimeoVideoComponent extends HTMLElement {
  connectedCallback() {
    if (!window.vimeoApiLoaded) {
      const interval = setInterval(() => {
        if (window.vimeoApiLoaded) {
          clearInterval(interval);
          this.initVimeoPlayer();
        }
      }, 100);
    } else {
      this.initVimeoPlayer();
    }
  }

  initVimeoPlayer() {
    const settings = JSON.parse(this.dataset.settings);
    const iframe = this.querySelector('.js-vimeo');
    const player = new Vimeo.Player(iframe);

    player.ready().then(() => {
      if (settings.muted) {
        player.setVolume(0);
      }
      if (settings.autoplay) {
        player.play();
      }
    });

    player.on('play', () => {
      const playBtn = this.querySelector('.vimeo-play-pause');
      if (playBtn) playBtn.innerHTML = window.ICON_SVGS.pause;
    });
    player.on('pause', () => {
      const playBtn = this.querySelector('.vimeo-play-pause');
      if (playBtn) playBtn.innerHTML = window.ICON_SVGS.play;
    });

    const playBtn = this.querySelector('.vimeo-play-pause');
    if (playBtn) {
      playBtn.addEventListener('click', () => {
        player.getPaused().then(paused => {
          if (paused) {
            player.play();
          } else {
            player.pause();
          }
        });
      });
    }

    const muteBtn = this.querySelector('.vimeo-mute-unmute');
    if (muteBtn) {
      muteBtn.addEventListener('click', () => {
        player.getVolume().then(volume => {
          if (volume > 0) {
            player.setVolume(0);
            muteBtn.innerHTML = window.ICON_SVGS.mute;
          } else {
            player.setVolume(1);
            muteBtn.innerHTML = window.ICON_SVGS.sound;
          }
        });
      });
    }
  }
}
customElements.define('vimeo-video', VimeoVideoComponent);
